import { useMemo, useRef } from 'react';

import { useLocation } from 'react-router-dom';

export type ParamKeyValuePair = [string, string];
export type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | URLSearchParams;

/**
 * Fork of useSearchParams from react-router v6.3.0.
 * See https://github.com/remix-run/react-router/blob/382c792ad8feb9a4d77a8046143982ba8bd0714f/packages/react-router-dom/index.tsx#L429-L471
 * A convenient wrapper for reading search parameters via the URLSearchParams interface.
 */
export function useSearchParams(defaultInit?: URLSearchParamsInit) {
    const defaultSearchParamsRef = useRef(createSearchParams(defaultInit));

    const location = useLocation();
    const searchParams = useMemo(() => {
        const searchParams = createSearchParams(location.search);
        for (const key of defaultSearchParamsRef.current.keys()) {
            if (!searchParams.has(key)) {
                for (const value of defaultSearchParamsRef.current.getAll(key)) {
                    searchParams.append(key, value);
                }
            }
        }

        return searchParams;
    }, [location.search]);

    return [searchParams] as const;
}

/**
 * Creates a URLSearchParams object using the given initializer.
 *
 * This is identical to `new URLSearchParams(init)` except it also
 * supports arrays as values in the object form of the initializer
 * instead of just strings. This is convenient when you need multiple
 * values for a given key, but don't want to use an array initializer.
 *
 * For example, instead of:
 *
 *   let searchParams = new URLSearchParams([
 *     ['sort', 'name'],
 *     ['sort', 'price']
 *   ]);
 *
 * you can do:
 *
 *   let searchParams = createSearchParams({
 *     sort: ['name', 'price']
 *   });
 */
export function createSearchParams(init: URLSearchParamsInit = ''): URLSearchParams {
    return new URLSearchParams(
        typeof init === 'string' || Array.isArray(init) || init instanceof URLSearchParams
            ? init
            : Object.keys(init).reduce((memo, key) => {
                  const value = init[key];
                  return memo.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
              }, [] as ParamKeyValuePair[]),
    );
}
